/**
 * ExpedientesSearchPage
 * Pantalla de busqueda de expedientes por telefono, directoryId o peopleId.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/constants';
import { useExpedientesSearch } from '../../hooks';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Expedientes', path: '#' },
];

export function ExpedientesSearchPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [submittedQ, setSubmittedQ] = useState('');
  const { results, isLoading, isError } = useExpedientesSearch(submittedQ, submittedQ.length >= 2);

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    setSubmittedQ(q.trim());
  }

  return (
    <div>
      <PageHeader breadcrumbs={BREADCRUMBS} title="Buscar Expediente" />

      <Card>
        <div className="p-6 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Teléfono, Directory ID o People ID"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" isLoading={isLoading}>Buscar</Button>
          </form>

          {isError && (
            <p className="text-sm text-red-600">Error al buscar. Intenta de nuevo.</p>
          )}

          {submittedQ && !isLoading && results.length === 0 && (
            <p className="text-sm text-gray-600">Sin resultados para "{submittedQ}"</p>
          )}

          {results.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase text-gray-500">Resultados ({results.length})</p>
              {results.map((r) => (
                <button
                  key={r.directory_id}
                  type="button"
                  onClick={() => navigate(ROUTES.EXPEDIENTE_DETAIL.replace(':directoryId', r.directory_id))}
                  className="w-full text-left p-4 border rounded-lg hover:bg-gray-50 transition"
                >
                  <p className="font-medium">{r.full_name}</p>
                  <p className="text-sm text-gray-600">
                    {r.phone} • {r.credit_level ?? 'Sin nivel'} • {r.has_active_credit ? 'Crédito activo' : 'Sin crédito activo'}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
